import { css } from "@emotion/react";
import styled from "@emotion/styled";
import arrayMutators from "final-form-arrays";
import { omit } from "lodash";
import { useState } from "react";
import * as React from "react";
import { Field, Form, FormSpy } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";

import { ChevronDown, ChevronUp } from "@livechat/design-system-icons";
import {
  ActionMenu,
  Button,
  DesignToken,
  Heading,
  Icon,
  ShadowToken,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import { FixedSaveBar, SaveBarMode } from "components/form";
import { FinalFormSwitch } from "components/form/FinalFormSwitch";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { isInIframe } from "helpers/app";
import { AutomationType } from "routes/automations/constants";
import { ActionPickerList } from "routes/automations/shared/actions/ActionPickerList";
import { validateActions, validateTriggers } from "routes/automations/shared/validators";
import { selectAgentsAlphabetically } from "store/entities/agents";
import { selectCannedResponsesArray } from "store/entities/canned-responses";
import { selectMailboxesAlphabetically } from "store/entities/mailboxes/selectors";
import { RulesActions } from "store/entities/rules/actions";
import { IRule } from "store/entities/rules/interfaces";
import {
  selectRuleCreateLoading,
  selectRulesMaxOrdering,
  selectRulesUpdateLoading,
} from "store/entities/rules/selectors";
import { selectTagsAlphabetically } from "store/entities/tags";
import { selectTeamsAlphabetically } from "store/entities/teams";
import { withVar } from "themes/helpers";

import { OpenedAccordionIndex } from "../../interfaces";
import { RuleEditorContainer } from "../styled";
import { TriggerPickerList } from "../triggers/TriggerPickerList";

import { DescriptionInput } from "./DescriptionInput";
import { NameInput } from "./NameInput";
import { IRuleForm } from "./types";

export interface IWorkflowPartComponentProps {
  rule: IRuleForm;
  index: number;
  changeRule: (rule: IRuleForm) => void;
}

interface IRuleEditorProps {
  rule: IRule;
  fromTemplate: string | null;
  duplicated?: string;
  setFormPristine: React.Dispatch<React.SetStateAction<boolean>>;
  handleBackToList: (pristine: boolean) => void;
}

const RuleEditor: React.FC<IRuleEditorProps> = ({
  rule,
  fromTemplate,
  duplicated,
  setFormPristine,
  handleBackToList,
}) => {
  const actionPickerRefs = React.useRef<HTMLDivElement[]>([]);
  const triggerPickerRefs = React.useRef<HTMLDivElement[]>([]);

  const allTeams = useSelector(selectTeamsAlphabetically);
  const allAgents = useSelector(selectAgentsAlphabetically);
  const allTags = useSelector(selectTagsAlphabetically);
  const allCannedResponses = useSelector(selectCannedResponsesArray);
  const allMailboxes = useSelector(selectMailboxesAlphabetically);
  const isCreating = useSelector(selectRuleCreateLoading);
  const isUpdating = useSelector(selectRulesUpdateLoading);

  const [editedRule] = useState<IRuleForm>({ ...rule });
  const [quantifierMenuOpen, setQuantifierMenuOpen] = useState(false);
  const [openedAccordionIndex, setOpenedAccordionIndex] = useState<OpenedAccordionIndex>(null);

  const submitLoading = isCreating || isUpdating;
  const embeddedInIframe = isInIframe();

  const maxOrdering = useSelector(selectRulesMaxOrdering);
  const dispatch = useDispatch();
  const updateRule = (updatedRule: IRule): void => {
    const ruleToSend = omit(updatedRule, "licenseId", "ordering", "premadeType", "useCounter", "type");
    dispatch(RulesActions.update({ value: { ...ruleToSend } }));
  };

  const createRule = (createdRule: IRule): void => {
    const ruleToSend = omit(createdRule, "id", "licenseId", "ordering", "premadeType", "useCounter");
    dispatch(
      RulesActions.create({
        value: { ...ruleToSend, ordering: maxOrdering + 100 },
        fromTemplate,
        duplicated,
      })
    );
  };

  const handleSubmit = (values: IRuleForm): void => {
    if (rule.id) {
      updateRule({ ...editedRule, ...values } as IRule);
    } else {
      createRule({ ...editedRule, ...values } as IRule);
    }
  };

  return (
    <Form<IRuleForm>
      onSubmit={handleSubmit}
      mutators={{
        ...arrayMutators,
      }}
      initialValues={editedRule}
      validate={(
        values
      ): { actions: ReturnType<typeof validateActions>; triggers: ReturnType<typeof validateTriggers> } => ({
        actions:
          !values.actions || values.actions?.length === 0
            ? ["At least one action must be added."]
            : validateActions(values.actions, allTeams, allAgents, allCannedResponses, allTags),
        triggers:
          !values.triggers || values.triggers?.length === 0
            ? ["At least one condition must be added."]
            : validateTriggers(values.triggers, allTags, allTeams, allAgents, allMailboxes),
      })}
      render={({ handleSubmit, pristine, values, form, invalid, submitFailed, dirtySinceLastSubmit, errors }) => {
        const handleQuantifierClick = (quantifier: "all" | "any"): void => {
          setQuantifierMenuOpen(false);
          form.change("quantifier", quantifier);
        };

        const scrollToFirstErrorOnSubmit = (): void => {
          const firstActionWithError = (errors?.actions as string[] | undefined)?.findIndex(
            error => error !== undefined
          );
          const firstTriggerWithError = (errors?.triggers as string[] | undefined)?.findIndex(
            error => error !== undefined
          );

          if (firstActionWithError !== undefined && actionPickerRefs.current[firstActionWithError]) {
            setTimeout(() => {
              actionPickerRefs.current[firstActionWithError]?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 0);
          } else if (firstTriggerWithError !== undefined && triggerPickerRefs.current[firstTriggerWithError]) {
            setTimeout(() => {
              triggerPickerRefs.current[firstTriggerWithError]?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 0);
          }

          handleSubmit();
        };

        return (
          <RuleEditorContainer>
            <form onSubmit={scrollToFirstErrorOnSubmit}>
              <Flex alignItems="center" gap={withVar(SpacingToken.Spacing2)} css={actionMenuStyles}>
                <Heading size="md">When</Heading>
                <ActionMenu
                  placement="bottom-start"
                  visible={quantifierMenuOpen}
                  onClose={() => setQuantifierMenuOpen(false)}
                  triggerRenderer={
                    <Button
                      type="button"
                      kind="secondary"
                      iconPosition="right"
                      icon={<Icon source={quantifierMenuOpen ? ChevronUp : ChevronDown} />}
                      onClick={() => setQuantifierMenuOpen(prev => !prev)}
                    >
                      {values.quantifier}
                    </Button>
                  }
                  options={[
                    {
                      key: "all",
                      onClick: () => handleQuantifierClick("all"),
                      element: (
                        <Flex column>
                          <Text bold>All</Text>
                          <Text size="xs" customColor={withVar(DesignToken.ContentBasicSecondary)}>
                            All conditions must be met to trigger the rule
                          </Text>
                        </Flex>
                      ),
                    },
                    {
                      key: "any",
                      onClick: () => handleQuantifierClick("any"),
                      element: (
                        <Flex column>
                          <Text bold>Any</Text>
                          <Text size="xs" customColor={withVar(DesignToken.ContentBasicSecondary)}>
                            At least one condition must be met to trigger the rule
                          </Text>
                        </Flex>
                      ),
                    },
                  ]}
                />
                <Heading size="md">of the following conditions are met…</Heading>
              </Flex>
              <Block marginBottom={withVar(SpacingToken.Spacing10)} marginTop={withVar(SpacingToken.Spacing2)}>
                <TriggerPickerList
                  triggerPickerRefs={triggerPickerRefs}
                  openedAccordionIndex={openedAccordionIndex}
                  setOpenedAccordionIndex={setOpenedAccordionIndex}
                />
              </Block>
              <Heading size="md">Then, take the following actions…</Heading>
              <Block marginBottom={withVar(SpacingToken.Spacing10)} marginTop={withVar(SpacingToken.Spacing2)}>
                <ActionPickerList
                  actionPickerRefs={actionPickerRefs}
                  limitReached={false}
                  openedAccordionIndex={openedAccordionIndex}
                  setOpenedAccordionIndex={setOpenedAccordionIndex}
                  automationType={AutomationType.Rule}
                />
              </Block>
              <Divider />
              <Heading size="xs">Describe rule</Heading>
              <Field
                name="name"
                validate={value => (value ? undefined : "Enter the required rule name")}
                render={({ input, meta }) => <NameInput input={input} meta={meta} />}
              />
              <Field name="description" render={({ input }) => <DescriptionInput input={input} />} />
              <Flex alignItems="center" maxWidth="640px">
                <Field type="checkbox" name="active" component={FinalFormSwitch} />
                <Text bold css={{ marginLeft: withVar(SpacingToken.Spacing2) }}>
                  {values.active ? "Turn off" : "Turn on"}
                </Text>
              </Flex>
              <FixedSaveBar
                embeddedInIframe={embeddedInIframe}
                onSave={scrollToFirstErrorOnSubmit}
                customSaveLabel={rule.id ? "Update rule" : "Create rule"}
                onCancel={() => handleBackToList(pristine)}
                mode={
                  pristine && !duplicated && !fromTemplate
                    ? SaveBarMode.Hidden
                    : submitLoading
                      ? SaveBarMode.Saving
                      : SaveBarMode.OfferSave
                }
                saveDisabled={invalid && submitFailed && !dirtySinceLastSubmit}
              />
            </form>
            <FormSpy subscription={{ pristine: true }} onChange={({ pristine }) => setFormPristine(pristine)} />
          </RuleEditorContainer>
        );
      }}
    />
  );
};

const Divider = styled.div`
  width: 100%;
  max-width: 644px;
  height: 1px;
  box-shadow: var(${ShadowToken.DividerBottom});
  margin-bottom: var(${SpacingToken.Spacing6});
`;

const actionMenuStyles = css`
  [class*="lc-ActionMenu"] {
    max-width: 400px;
  }
`;

export default RuleEditor;
